import { Box, Button, Flex, Text } from "@chakra-ui/react";
import {
  CHAT_RESPONSE_STREAM_TIME,
  CHAT_STATUS_GENERAL,
  CHAT_TYPE,
  DOCUMENT_TYPE,
  LEGAL_CHATMODE,
  PAYLOAD_TYPE,
  ROUTE_NAMES,
} from "@root/constants";
import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  useDeferredValue,
} from "react";
import { getThreadList } from "@root/store/selectors/entities/thread";
import { useDispatch, useSelector } from "react-redux";
import {
  doQuery,
  doQueryDrawingAnalysis,
  doQueryKCS,
  doQueryMultiDoc,
  doQueryProject,
  verifyChat,
} from "@root/store/thunks/chatThunks";
import { getThreads } from "@root/store/thunks";
import { useMainChatContext } from "@root/context/kps-chat-context";
import MarkdownContent from "@root/components/markdown/markdown";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useTypewriterEffect from "./useTypewriterEffect";
import ChatSourceData from "@root/pages/dashboard/components/ChatSourceData";
import { useProjectsContext } from "@root/context/projects-context";
import ChatFollowUpQuestions from "@root/pages/dashboard/components/ChatFollowUpQuestions";
import { getTransformedAnswer, parseChatAnswer } from "@root/utils/chatHelper";
import ShimmeringStatusText from "@root/components/common/text/ShimmeringStatusText";
import { InfoIcon } from "@root/assets/icons/infoicon";
import { WarningIcon } from "@root/assets/icons/warning-icon";
import SystemChatActions from "./SystemChatActions";
import { safeJsonParse } from "@root/utils";
import useVerifyChat, { getGlobalIsVerifying } from "@root/hooks/useVerifyChat";
import { getCurrentLanguage } from "@root/store/selectors/features";

function SystemChat({ chat, isLastSystemMessage = false }: any) {
  const {
    currentMessageResponse,
    setCurrentMessageResponse,
    setIsMessaging,
    input,
    setIsWritingFirstPatch,
    setUrls,
    setUrlType,
    currentPathname,
    getSource,
    getMode,
    getFilesPath,
    getDocNames,
    getPdfPageNum,
    downloadPdfOfThread,
    getFileNameMain,
    getFileNamesArray,
    setIsTypewriterActive,
    getModel,
    attachedFile,
    legalChatMode,
    setIsPolling,
    getPdfId,
  } = useMainChatContext();

  const { getFilename } = useProjectsContext();
  const { pathname } = useLocation();
  const isKCS = pathname.includes("kcs");
  const isProjectChat = pathname.includes(ROUTE_NAMES.DOCUMENT_CHAT);
  const isLegalChatbot = pathname.startsWith("/chat/");
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const isWebSearch = legalChatMode === LEGAL_CHATMODE.WEB_SEARCH;
  const legalWebSearchScenario = isWebSearch && !attachedFile?.length;

  // Memoize options to prevent hook recreation
  const verifyChatOptions = useMemo(
    () => ({
      onSuccess: (data: any) => {
        console.log("verifychat success data: ", data);
      },
      onError: (error: any) => {
        console.error("verifychat error: ", error);
      },
    }),
    []
  );

  // Use verify chat hook
  const { handleVerifyChat: handleVerifyChatClick, isVerifying } =
    useVerifyChat(verifyChatOptions);

  const { error, editLoading } = chat;
  const exportChecklistLabel = t("systemChat.exportChecklistButton");

  const output = chat?.output || chat?.meta?.answer;
  const [answer, setAnswer] = useState(output || "");

  const isMultiAnswer = Array.isArray(answer) && answer?.length > 0;

  const transformedAnswer = useMemo(() => {
    // For multiple answers
    if (isMultiAnswer) {
      return getTransformedAnswer(answer);
    }
    // For single answers, return as-is without blockquote wrapper
    return answer;
  }, [answer]);

  const [sources, setSources] = useState(chat?.meta?.sources || []);
  const [followUpQuestions, setFollowUpQuestions] = useState(
    chat?.meta?.follow_up || []
  );
  const [currentInterval, setCurrentInterval] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState(output || "");
  const [currentStatus, setCurrentStatus] = useState(output ? "stop" : "null");
  const [isMessageCompleted, setIsMessageCompleted] = useState(false);
  const [index, setIndex] = useState(0);
  const typingTimeoutRef: any = useRef(null);
  const isProcessingNewText = useRef(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const threadList: any = useSelector(getThreadList);
  const currentChat: any = threadList[threadList?.length - 1];
  const [currentError, setCurrentError] = useState<any>(null);
  const [currentID, setCurrentID] = useState(null);
  const [isGenerationStopped, setIsGenerationStopped] = useState(false);
  // Track consecutive status polling to detect stuck polling scenarios
  const consecutiveStatusRef = useRef<{
    status: number | null;
    count: number;
  }>({ status: null, count: 0 });
  const [isConsecutivePollingError, setIsConsecutivePollingError] =
    useState(false);
  const chatKuId = chat?.meta?.chatKuid || chat?.meta?.kuid;
  const userInput =
    input ||
    currentMessageResponse?.question ||
    threadList?.[threadList?.length - 1]?.input ||
    threadList?.[threadList?.length - 2]?.input;

  // Returns response structure similar to how the backend API returns it
  const lastOriginalResponse = useMemo(() => {
    for (let i = threadList?.length - 1; i >= 0; i--) {
      if (threadList?.[i]?.role === "assistent") {
        const lastResponse = threadList?.[i];
        if (lastResponse?.meta?.c) {
          return lastResponse?.meta?.c;
        }
        return lastResponse?.meta;
      }
    }
    return null;
  }, [threadList]);

  const showVerifyButton = useMemo(() => {
    if (lastOriginalResponse && isLastSystemMessage) {
      return safeJsonParse(lastOriginalResponse?.answer)?.verify;
    }
    return false;
  }, [lastOriginalResponse]);

  const navigate = useNavigate();
  const { id } = useParams();
  const currentChatKuid = currentMessageResponse?.chatKuid || chatKuId;

  const searchStatus = currentMessageResponse?.search_status;
  const webpageStatus = currentMessageResponse?.webpage_status;
  const fileStatus = currentMessageResponse?.file_status;
  const youtubeStatus = currentMessageResponse?.youtube_status;
  const deepSearchStatus = currentMessageResponse?.deepsearch_status;
  const type = currentMessageResponse?.type;
  const chatItem = chat?.meta?.c || chat?.meta;
  const currentLanguage = useSelector(getCurrentLanguage);
  const { documentData } = useProjectsContext();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const chatType = useMemo(() => {
    const rawType = chat?.meta?.c?.type ?? chat?.meta?.type;
    return rawType ? rawType : null;
  }, [chat?.meta?.c?.type, chat?.meta?.type]);

  // Check if current chat is from verifyChat API
  // Only check the current chat item being rendered, not the entire threadList
  const isVerifyChatResponse = useMemo(() => {
    // Check if the current chat item has isVerify flag set to true
    // This is how verifyChat items are identified (from useVerifyChat hook)
    return (
      chat?.isVerify === true ||
      chatItem?.isVerify === true ||
      chat?.meta?.c?.isVerify === true ||
      chat?.meta?.isVerify === true ||
      chatType === CHAT_TYPE.VERIFICATION
    );
  }, [
    chat?.isVerify,
    chatItem?.isVerify,
    chat?.meta?.c?.isVerify,
    chat?.meta?.isVerify,
    chatType,
  ]);

  // Extract only sources that are actually linked inline in the answer text
  const displayedSources = useMemo(() => {
    if (!sources?.length) return [];
    const answerText = typeof transformedAnswer === "string" ? transformedAnswer : "";
    // Match all markdown link URLs: [text](url)
    const inlineUrlRegex = /\[[^\]]*\]\(([^)]+)\)/g;
    const inlineUrls = new Set<string>();
    let match;
    while ((match = inlineUrlRegex.exec(answerText)) !== null) {
      inlineUrls.add(match[1].trim());
    }
    if (!inlineUrls.size) return [];
    // Filter sources to only those whose URL appears inline, deduplicated
    const seen = new Set<string>();
    return sources.filter((source: any) => {
      const url = typeof source === "object" ? (source?.signedUrl || source?.url || "") : source;
      if (!inlineUrls.has(url)) return false;
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
  }, [sources, transformedAnswer]);

  // Custom link click handler
  const handleLinkClick = useCallback(
    (text: string, url: string) => {
      if (sources?.length) {
        if (isKCS) {
          const currentDocumentId = text?.split("_")?.[0];
          const sourceItem = sources?.find(
            (item: any) => item?.documentId === currentDocumentId
          );
          if (sourceItem) {
            window.open(sourceItem?.signedUrl, "_blank");
          }
        } else if (isProjectChat) {
          const sourceItem = sources?.find(
            (item: any) => item?.filename?.split("/")?.pop() === text
          );
          if (sourceItem) {
            window.open(sourceItem?.signedUrl, "_blank");
          }
        } else if (isLegalChatbot) {
          const sourceItem = sources?.find((item: any) => item == url);
          if (sourceItem) {
            window.open(sourceItem, "_blank");
          }
        }
      }
    },
    [sources]
  );

  const CHAT_STATUS = useMemo(() => {
    // if (currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
    //   return DRAWING_ANALYSIS_CHAT_STATUS;
    // }
    return CHAT_STATUS_GENERAL;
  }, [currentPathname]);

  // Centralized Status Management
  const statusManager = useMemo(() => {
    const chatMetaStatus = chat?.meta?.status;
    const chatMetaCStatus = chat?.meta?.c?.status; // Status from meta.c (used by verify-chat)

    const currentMessageStatus = currentMessageResponse?.status;
    const chatItemStatus = chatItem?.status;

    // For verify-chat items, prioritize status from chat?.meta?.c?.status (where verify-chat stores it)
    // For regular chat items, use currentMessageResponse status or chat meta status
    // Primary status priority: verify-chat status > currentMessageResponse > chatMetaStatus
    const primaryStatus = isVerifyChatResponse
      ? chatMetaCStatus || chatItemStatus || chatMetaStatus
      : currentMessageStatus || chatMetaStatus || chatItemStatus;

    return {
      // Status constants for easy reference
      STATUS: CHAT_STATUS,

      // Current status values
      current: {
        chatMeta: chatMetaStatus,
        chatMetaC: chatMetaCStatus, // Status from meta.c (verify-chat)
        messageResponse: currentMessageStatus,
        chatItem: chatItemStatus,
        primary: primaryStatus,
      },

      // Status checks
      isProcessing: () => CHAT_STATUS.PROCESSING.includes(primaryStatus),
      isCompleted: () =>
        primaryStatus === CHAT_STATUS.COMPLETED ||
        chatItemStatus === CHAT_STATUS.COMPLETED ||
        primaryStatus === CHAT_STATUS.NO_CHANGE ||
        chatItemStatus === CHAT_STATUS.NO_CHANGE,
      hasError: () =>
        primaryStatus === CHAT_STATUS.ERROR ||
        chatItemStatus === CHAT_STATUS.ERROR ||
        currentError !== null ||
        error !== undefined,
      isPending: () => primaryStatus === CHAT_STATUS.PENDING,
      isInProgress: () => CHAT_STATUS.IN_PROGRESS.includes(primaryStatus),
      isNearComplete: () => primaryStatus === CHAT_STATUS.NEAR_COMPLETE,

      // Loading state check
      // Don't show loading if there's a consecutive polling error (should show error instead)
      isLoading: () =>
        !isConsecutivePollingError &&
        chatItemStatus !== CHAT_STATUS.COMPLETED &&
        chatItemStatus !== CHAT_STATUS.NEAR_COMPLETE &&
        chatItemStatus !== CHAT_STATUS.ERROR &&
        chatItemStatus !== CHAT_STATUS.NO_CHANGE,

      // Error state check
      isError: () =>
        chatItemStatus === CHAT_STATUS.ERROR ||
        currentError ||
        isConsecutivePollingError,

      // Should continue polling
      shouldContinuePolling: () => {
        const isProcessing = CHAT_STATUS.IN_PROGRESS.includes(primaryStatus);
        const isCompleted =
          primaryStatus === CHAT_STATUS.COMPLETED ||
          primaryStatus === CHAT_STATUS.NO_CHANGE;
        const hasError = primaryStatus === CHAT_STATUS.ERROR;
        return isProcessing || (!isCompleted && !hasError);
      },

      // Should start typing animation
      shouldStartTyping: () =>
        primaryStatus !== CHAT_STATUS.COMPLETED &&
        primaryStatus !== CHAT_STATUS.NEAR_COMPLETE &&
        primaryStatus !== CHAT_STATUS.NO_CHANGE &&
        currentStatus !== "start",

      // Should set completed state
      shouldSetCompleted: () =>
        primaryStatus === CHAT_STATUS.COMPLETED ||
        primaryStatus === CHAT_STATUS.NO_CHANGE,

      // Should navigate after completion
      shouldNavigateAfterCompletion: () =>
        (primaryStatus === CHAT_STATUS.COMPLETED ||
          primaryStatus === CHAT_STATUS.NO_CHANGE) &&
        !id,

      // Should fetch sources
      shouldFetchSources: () =>
        primaryStatus === CHAT_STATUS.COMPLETED ||
        primaryStatus === CHAT_STATUS.NO_CHANGE,

      // Should stop polling
      shouldStopPolling: () => {
        // Stop if consecutive polling error detected
        if (isConsecutivePollingError) {
          return true;
        }
        // For verify-chat, check meta.c status; otherwise check meta status
        const statusToCheck = isVerifyChatResponse
          ? chatMetaCStatus || chatItemStatus
          : chatMetaStatus;
        return (
          statusToCheck === CHAT_STATUS.ERROR ||
          statusToCheck === CHAT_STATUS.COMPLETED ||
          statusToCheck === CHAT_STATUS.NO_CHANGE
        );
      },

      // Should set answer and typed answer
      shouldSetAnswerAndTyped: () => {
        // For verify-chat, check meta.c status; otherwise check meta status
        const statusToCheck = isVerifyChatResponse
          ? chatMetaCStatus || chatItemStatus
          : chatMetaStatus;
        return (
          chat?.output &&
          (statusToCheck === CHAT_STATUS.COMPLETED ||
            statusToCheck === CHAT_STATUS.NO_CHANGE)
        );
      },
    };
  }, [
    chat?.meta?.status,
    chat?.meta?.c?.status, // Include verify-chat status in dependencies
    currentMessageResponse?.status,
    chatItem?.status,
    isVerifyChatResponse, // Include verify-chat flag to react to changes
    type,
    deepSearchStatus,
    searchStatus,
    webpageStatus,
    fileStatus,
    youtubeStatus,
    id,
    chat?.output,
    CHAT_STATUS,
    currentError,
    error,
    isConsecutivePollingError,
  ]);
  // Cleanup all polling and timers on unmount
  useEffect(() => {
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;

      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // Clear polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Reset consecutive status tracking
      consecutiveStatusRef.current = { status: null, count: 0 };
      setIsConsecutivePollingError(false);
    };
  }, []);

  // Reset consecutive tracking when chat changes
  useEffect(() => {
    consecutiveStatusRef.current = { status: null, count: 0 };
    setIsConsecutivePollingError(false);
  }, [chat?.chatId, chat?.meta?.id, chat?.meta?.c?.id]);

  useEffect(() => {
    if (currentStatus === "start") {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (index < answer?.length) {
        typingTimeoutRef.current = setTimeout(() => {
          // Don't update state if component is unmounted
          if (!isMountedRef.current) {
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = null;
            }
            return;
          }
          setTypedAnswer((prev: any) => prev + answer[index]);
          setIndex((prev) => prev + 1);
        }, 5);
      } else if (isMessageCompleted) {
        // Don't update state if component is unmounted
        if (!isMountedRef.current) return;

        setCurrentStatus("stop");
        setUrls([]);
        setUrlType(null);
      }
    }
  }, [index, answer, currentStatus, isMessageCompleted, setUrls, setUrlType]);

  useEffect(() => {
    if (isProcessingNewText.current || !answer) return;
    isProcessingNewText.current = true;
    try {
      if (statusManager.isCompleted() || statusManager.isNearComplete()) {
        setTypedAnswer(answer);
      } else if (answer?.length > typedAnswer?.length) {
        if (currentStatus !== "start") {
          setTypedAnswer("");
          setIndex(0);
          setCurrentStatus("start");
        }
      }
    } finally {
      isProcessingNewText.current = false;
    }
  }, [answer, statusManager.current.primary]);

  function getPayload(type: string) {
    return {
      query: userInput,
      threadId: currentMessageResponse?.threadId || undefined,
      // source: getSource(),
      mode: getMode()?.toString(),
      project_id: getMode(),
      s3_file_path: getFilesPath(),
      doc_name: getDocNames(),
      fileName: getFileNameMain(),
      file_name: getFileNamesArray(), // Currently relevant in project document chatbot
      page_no: getPdfPageNum(),
      pdf_id: getPdfId(),
      // model: getModel(),
      chatId:
        chat?.chatId ||
        currentMessageResponse?.chatId ||
        currentMessageResponse?.id,
    };
  }

  const fetchChat = useCallback(
    (type: string = PAYLOAD_TYPE.DEFAULT) => {
      // Don't fetch if component is unmounted
      if (!isMountedRef.current) {
        return;
      }

      // Don't fetch if verify-chat is in progress (check global state)
      // This prevents ANY SystemChat instance from calling fetchChat during verify
      if (isVerifying || getGlobalIsVerifying()) {
        return;
      }

      if (
        !chat?.chatId &&
        !currentMessageResponse?.chatId &&
        !currentMessageResponse?.id
      ) {
        return;
      }
      if (statusManager.isCompleted()) {
        return;
      }
      // Don't reset consecutive polling error here - it should persist until chat changes
      // Only reset if it's a different type of error
      if (currentError?.type !== "CONSECUTIVE_POLLING_TIMEOUT") {
        setCurrentError(null);
      }
      if (
        !statusManager.isCompleted() &&
        !statusManager.isNearComplete() &&
        currentStatus !== "start"
      ) {
        setCurrentStatus("start");
      }

      const payload = {
        payload: getPayload(type),
        onError: (error: any) => {
          // Don't update state if component is unmounted
          if (!isMountedRef.current) return;

          setCurrentStatus("null");
          setCurrentError(error);
          setIsMessaging(false);
          setIsWritingFirstPatch(false);
        },
        onSuccess: (data: any) => {
          // Don't update state if component is unmounted
          if (!isMountedRef.current) return;

          const receivedStatus = data?.status;
          const STATUS_NEAR_COMPLETE = statusManager.STATUS.NEAR_COMPLETE; // Status 3

          // Track consecutive status polling (EXCLUDING status 3 completely)
          // Status 3 means we are receiving data, so it should never trigger timeout error
          if (receivedStatus !== undefined && receivedStatus !== null) {
            // IMPORTANT: Status 3 (NEAR_COMPLETE/STREAMING) is completely excluded from tracking
            // Even if we receive 100+ consecutive status 3 responses, we should NOT show error
            if (receivedStatus === STATUS_NEAR_COMPLETE) {
              // Reset counter when status 3 is received (streaming is active, data is flowing)
              // Status 3 means AI is responding, so reset any consecutive tracking
              consecutiveStatusRef.current = { status: null, count: 0 };
              setIsConsecutivePollingError(false);
              // Clear any existing consecutive polling error since we're receiving data
              if (currentError?.type === "CONSECUTIVE_POLLING_TIMEOUT") {
                setCurrentError(null);
              }
            } else {
              // Only track non-status-3 statuses for consecutive counting
              // Track consecutive same status (status 3 is never stored or counted)
              if (
                consecutiveStatusRef.current.status === receivedStatus
              ) {
                consecutiveStatusRef.current.count += 1;
              } else {
                // Status changed (and it's not status 3), reset counter and start tracking new status
                consecutiveStatusRef.current = {
                  status: receivedStatus,
                  count: 1,
                };
              }

              // Check if we've exceeded the threshold (only for non-status-3 statuses)
              if (
                consecutiveStatusRef.current.count >=
                CHAT_RESPONSE_STREAM_TIME.MAX_CONSECUTIVE_SAME_STATUS
              ) {
                setIsConsecutivePollingError(true);
                setCurrentError({
                  type: "CONSECUTIVE_POLLING_TIMEOUT",
                });
                // Stop polling
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
                setIsPolling(false);
              }
            }
          }

          setIsMessageCompleted(
            receivedStatus === statusManager.STATUS.COMPLETED ||
            receivedStatus === statusManager.STATUS.NO_CHANGE
          );
          setCurrentMessageResponse(data);
          setCurrentID(data?.kuid);
          if (
            receivedStatus === statusManager.STATUS.COMPLETED ||
            receivedStatus === statusManager.STATUS.NO_CHANGE
          ) {
            setIsMessaging(false);
            setCurrentInterval(0);
            // Reset consecutive tracking on completion
            consecutiveStatusRef.current = { status: null, count: 0 };
            setIsConsecutivePollingError(false);
            // Refetch threads on successful polling completion
            // dispatch(
            //   getThreads({
            //     onSuccess: (res: any) => dispatch(updateThreads(res)),
            //   })
            // );
            if (data?.threadId && !id) {
              if (currentPathname?.includes(DOCUMENT_TYPE.DOCUMENT)) {
                navigate(`/document-chat/${data?.threadId}`, { replace: true });
              } else if (currentPathname?.includes(DOCUMENT_TYPE.CHECKLIST)) {
                navigate(`/checklist-chat/${data?.threadId}`, {
                  replace: true,
                });
              } else if (
                currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)
              ) {
                navigate(`/drawing-analysis-chat/${data?.threadId}`, {
                  replace: true,
                });
              } else if (currentPathname?.includes(ROUTE_NAMES.KCS)) {
                navigate(`/kcs-chat/${data?.threadId}`, {
                  replace: true,
                });
              } else {
                navigate(`/chat/${data?.threadId}`, { replace: true });
              }
              // Update sidebar threads after navigation
              // Delay API call to ensure the new thread is persisted in the database
              setTimeout(() => {
                dispatch(
                  getThreads({
                    params: { page: 1 },
                    onSuccess: (res: any) => {
                      console.log("getThreads API response after navigation:", res);
                    },
                  })
                );
              }, 5000); // Need to wait for the new thread to be persisted in the database
            }
          }
          // Parse the answer using the reusable utility function
          const ans = parseChatAnswer(data);
          setAnswer(ans);
          if (
            receivedStatus === statusManager.STATUS.COMPLETED ||
            receivedStatus === statusManager.STATUS.NO_CHANGE
          ) {
            setTypedAnswer(ans);
            try {
              const fetchedSources = JSON.parse(data?.answer || "{}")?.sources;
              const fetchedFollowUpQuestions = JSON.parse(
                data?.answer || "{}"
              )?.follow_up;
              setSources(fetchedSources);
              setFollowUpQuestions(fetchedFollowUpQuestions);
            } catch (e) {
              setSources([]);
              setFollowUpQuestions([]);
            }
          }
          setCurrentMessageResponse({ ...currentMessageResponse, ...data });
          setIsWritingFirstPatch(false);
        },
      };
      if (type === PAYLOAD_TYPE.VERIFY) {
        dispatch(verifyChat(payload));
      } else if (currentPathname?.includes(ROUTE_NAMES.KCS)) {
        dispatch(doQueryKCS(payload));
      } else if (currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
        dispatch(doQueryDrawingAnalysis(payload));
      } else if (currentPathname?.includes("document")) {
        dispatch(doQueryProject(payload));
      } else if (currentPathname?.includes("checklist")) {
        dispatch(doQueryMultiDoc(payload));
      } else {
        dispatch(doQuery(payload));
      }
    },
    [
      chat?.chatId,
      currentMessageResponse?.chatId,
      currentMessageResponse?.id,
      currentMessageResponse?.threadId,
      statusManager,
      currentStatus,
      userInput,
      getSource,
      getMode,
      getFilesPath,
      getDocNames,
      getFileNameMain,
      getFileNamesArray,
      getPdfPageNum,
      documentData?.id,
      currentPathname,
      dispatch,
      setCurrentError,
      setCurrentStatus,
      setIsMessaging,
      setIsWritingFirstPatch,
      setCurrentMessageResponse,
      setCurrentID,
      setCurrentInterval,
      setIsMessageCompleted,
      setAnswer,
      setTypedAnswer,
      setSources,
      answer,
      id,
      navigate,
      isVerifying,
    ]
  );

  const currentResponseId =
    currentMessageResponse?.chatId || currentMessageResponse?.id;
  const currentChatId = chat?.meta?.id || chat?.meta?.c?.id;
  const isChatIdEqual =
    !!(currentResponseId && currentChatId) &&
    currentResponseId === currentChatId;

  // For verify chat items, enable animation if it's the last message
  // Verify chat items don't update currentMessageResponse, so we need special handling
  const shouldEnableAnimation = useMemo(() => {
    // If it's a verify chat item and it's the last system message, enable animation
    if (getGlobalIsVerifying() && isLastSystemMessage) {
      return true;
    }
    // For regular chat items, use the existing logic
    return isChatIdEqual;
  }, [isVerifyChatResponse, isLastSystemMessage, isChatIdEqual]);

  const getStreamingDelay = () => {
    if (
      statusManager.isCompleted() &&
      pathname.startsWith(ROUTE_NAMES.KSCE_CHAT_SCREEN)
    ) {
      return 2;
    }
    if (statusManager.isCompleted()) {
      return 3;
    }
    return 20;
  };

  const { displayText, isComplete } = useTypewriterEffect(
    transformedAnswer || "",
    {
      delay: getStreamingDelay(),
      disableAnimation: !shouldEnableAnimation,
    }
  );
  // Let React defer heavy markdown renders during streaming for smoother typing
  const deferredDisplayText = useDeferredValue(displayText);

  // Update typewriter active state when typing status changes
  useEffect(() => {
    // Only track typewriter for the current/latest message
    // Include verify chat items in the check
    if (shouldEnableAnimation && !isComplete) {
      setIsTypewriterActive(true);
    } else if (isComplete) {
      setIsTypewriterActive(false);
    }
  }, [isComplete, shouldEnableAnimation, setIsTypewriterActive]);

  // Add click listener for Export Checklist button
  useEffect(() => {
    const handleExportClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Find the code block element that contains the export button
      // The button is rendered as a code block (```), so we need to check
      // the <pre><code> element or traverse up to find it
      let elementToCheck: HTMLElement | null = target;

      // Traverse up the DOM to find a pre/code element that contains the button text
      while (elementToCheck && elementToCheck !== chatContainerRef.current) {
        const textContent = elementToCheck.textContent || "";

        // Check if this element or any of its children contains the export button label
        if (textContent.includes(exportChecklistLabel)) {
          // Also check if we're within a code block (pre or code element)
          const codeBlock = elementToCheck.closest("pre, code, pre code");
          if (codeBlock) {
            // Prevent event bubbling to avoid multiple handlers
            event.stopPropagation();

            const threadId =
              chat?.meta?.threadId ||
              chat?.meta?.c?.threadId ||
              currentMessageResponse?.threadId;
            const chatId =
              chat?.meta?.id ||
              chat?.meta?.c?.id ||
              currentMessageResponse?.chatId ||
              currentMessageResponse?.id;
            if (threadId && chatId) {
              downloadPdfOfThread({
                threadId: threadId,
                chatId: chatId,
              });
            }
            return;
          }
        }
        elementToCheck = elementToCheck.parentElement;
      }
    };

    // Add event listener to the chat container for event delegation
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener("click", handleExportClick, true); // Use capture phase
    }

    // Cleanup
    return () => {
      if (container) {
        container.removeEventListener("click", handleExportClick, true);
      }
    };
  }, [
    chat?.meta?.id,
    chat?.meta?.c?.id,
    chat?.meta?.threadId,
    chat?.meta?.c?.threadId,
    currentMessageResponse?.threadId,
    currentMessageResponse?.chatId,
    currentMessageResponse?.id,
    exportChecklistLabel,
    downloadPdfOfThread,
  ]); // Include all relevant dependencies

  useEffect(() => {
    if (chat?.output && statusManager.isCompleted()) {
      setAnswer(chat?.output);
      setTypedAnswer(chat?.output);
      setCurrentStatus("stop");
    }
  }, [chat?.output, statusManager.current.primary]);

  // For verify chat items, update answer state as output changes during streaming
  // This ensures the answer state syncs with Redux store updates from useVerifyChat polling
  useEffect(() => {
    if (isVerifyChatResponse) {
      // Get the latest output value from chat prop (which comes from Redux store)
      const newOutput = chat?.output || chat?.meta?.answer || "";
      // Update answer state whenever output changes during verify chat streaming
      // This handles the case where useVerifyChat updates the Redux store via updateThreadChatById
      setAnswer(newOutput);
      // Also update sources and follow_up if they exist in meta
      // if (chat?.meta?.sources) {
      //   setSources(chat.meta.sources);
      // }
      // if (chat?.meta?.follow_up) {
      //   setFollowUpQuestions(chat.meta.follow_up);
      // }
    }
  }, [
    isVerifyChatResponse,
    chat?.output,
    chat?.meta?.answer,
    chat?.meta?.sources,
    chat?.meta?.follow_up,
  ]);

  // Calculate error and loading states before useEffect (used in dependencies)
  const isError = statusManager.isError();
  const isLoading = statusManager.isLoading();

  useEffect(() => {
    console.log("use effect test");

    // Don't start polling if component is unmounted
    if (!isMountedRef.current) {
      return;
    }

    // Don't start/continue polling if verify-chat is in progress (check global state)
    if (isVerifying || getGlobalIsVerifying()) {
      console.log("Polling stopped: verify-chat is in progress");
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    // Stop polling if consecutive polling error detected
    if (isConsecutivePollingError) {
      console.log("Polling stopped: consecutive polling timeout error");
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    if (chatKuId !== currentChatKuid || currentError || error || isError) {
      console.log("Polling stopped: chatKuId mismatch, error, or isError");
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    // For existing threads, don't poll if already completed
    if (statusManager.isCompleted() || statusManager.hasError()) {
      console.log("Polling stopped: thread already completed");
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    if (statusManager.shouldContinuePolling()) {
      console.log("Starting/continuing polling...");

      // Only start polling if not already completed or errored
      if (!statusManager.shouldStopPolling()) {
        // Clear any existing interval before setting a new one
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        if (CHAT_RESPONSE_STREAM_TIME.FIRST_REFETCH_WAIT < currentInterval) {
          console.log("Fetching chat...");
          fetchChat(PAYLOAD_TYPE.DEFAULT);
        }

        // Set polling state to true when polling starts
        setIsPolling(true);

        pollingIntervalRef.current = setInterval(() => {
          // Check if component is still mounted before fetching
          if (!isMountedRef.current) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            setIsPolling(false);
            return;
          }
          // Don't fetch if verify-chat is in progress (check global state)
          if (isVerifying || getGlobalIsVerifying()) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            setIsPolling(false);
            return;
          }
          console.log("chat?.meta?.c?.status", chat?.meta?.c?.status);
          fetchChat(PAYLOAD_TYPE.DEFAULT);
        }, CHAT_RESPONSE_STREAM_TIME.REFETCH_INTERVAL);
      }
    } else {
      console.log("Polling stopped: completed or error status");
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Set polling state to false when polling stops
      setIsPolling(false);
    }

    return () => {
      // Cleanup: clear polling interval on unmount or dependency change
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [
    currentChat?.meta?.status,
    error,
    currentError,
    chatKuId,
    currentChatKuid,
    isError,
    statusManager,
    currentInterval,
    fetchChat,
    isVerifying,
    setIsPolling,
    isConsecutivePollingError,
  ]);

  // if (false) {
  //   return (
  //     <Flex justifyContent={"center"} zIndex={9}>
  //       <Box marginLeft={"150px"} marginTop={"110px"}>
  //         <ChatLoader />
  //       </Box>
  //     </Flex>
  //   );
  // }

  // Check error first - errors should take precedence over loading state
  if (isError) {
    // Determine error message based on error type
    const errorMessage =
      isConsecutivePollingError ||
        currentError?.type === "CONSECUTIVE_POLLING_TIMEOUT"
        ? t("systemChat.consecutivePollingTimeoutMessage")
        : t("systemChat.errorMessage");

    return (
      <Box marginTop={"40px"} marginBottom={"100px"}>
        <Flex alignItems="center" gap="4px">
          <InfoIcon width={22} height={22} color="#E0282F" />
          <Text fontWeight={500} color={"#E0282F"}>
            {errorMessage}
          </Text>
        </Flex>
      </Box>
    );
  }

  // Show loading state only if not in error state
  if (isLoading) {
    return (
      <Box>
        <ShimmeringStatusText message={t("systemChat.waitMessage")} />
      </Box>
    );
  }

  return (
    <Box
      ref={chatContainerRef}
      position="relative"
      display="flex"
      flexDir="column"
      gap="16px"
    >
      {/* AI Chat */}
      {/* Question */}
      <Box>
        <Flex>
          <Box>
            <Flex gap="6px">
              {deferredDisplayText?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    color: "theme.gray.500",
                    wordBreak: "break-word",
                    width: "100%",
                  }}
                  color={"theme.gray.500"}
                >
                  <div
                    style={{
                      // marginTop: "-13px",
                      // marginLeft: "-10px",
                      width: "100%",
                    }}
                    data-chat-message={chat?.meta?.id || chat?.meta?.c?.id}
                  >
                    <MarkdownContent
                      content={deferredDisplayText || ""}
                      onLinkClick={handleLinkClick}
                      isMultiAnswer={isMultiAnswer}
                    />
                  </div>
                </div>
              )}
            </Flex>
          </Box>
        </Flex>
      </Box>
      {followUpQuestions?.length > 0 &&
        !isVerifyChatResponse &&
        chatType !== CHAT_TYPE.VERIFICATION && (
          <ChatFollowUpQuestions followUpQuestions={followUpQuestions} />
        )}
      {/* <ChatSources /> */}
      {displayedSources?.length > 0 && !isVerifyChatResponse && (
        <Flex
          flexDirection="row"
          justifyContent="flex-start"
          marginBottom={"10px"}
        >
          <ChatSourceData sources={displayedSources} />
        </Flex>
      )}
      {statusManager.isCompleted() && !isVerifyChatResponse && (
        <SystemChatActions
          answer={transformedAnswer}
          chat={chat}
        //onRefetch={refetchLikeStatus}  no need to requery
        />
      )}
      {(showVerifyButton || isVerifyChatResponse) &&
        legalWebSearchScenario &&
        isLegalChatbot &&
        isComplete &&
        statusManager.isCompleted() &&
        !getGlobalIsVerifying() && (
          <Flex justifyContent={"center"}>
            <Button
              bg="white"
              border="1px solid"
              borderColor="#A8A0FF"
              borderRadius="full"
              px="16px"
              py="10px"
              height="auto"
              minH="auto"
              boxShadow="0 2px 6px rgba(168, 160, 255, 0.25)"
              _hover={{
                bg: "white",
                borderColor: "#A8A0FF",
                boxShadow: "0 2px 6px rgba(168, 160, 255, 0.25)",
              }}
              _active={{
                bg: "white",
                borderColor: "#A8A0FF",
                boxShadow: "0 2px 6px rgba(168, 160, 255, 0.25)",
              }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="8px"
              fontWeight={500}
              fontSize="14px"
              color="#6941FF"
              lineHeight="1.2"
              width={"120px"}
              onClick={() => {
                const lastAnswer = safeJsonParse(lastOriginalResponse?.answer);
                handleVerifyChatClick({
                  query: lastOriginalResponse?.question,
                  threadId: lastOriginalResponse?.threadId,
                  chat_history: lastAnswer?.chat_history,
                  law_generated_answer: lastAnswer?.answer,
                  law_chat_id: lastOriginalResponse?.id,
                });
              }}
              isLoading={isVerifying}
              isDisabled={isVerifying || isVerifyChatResponse}
            >
              <WarningIcon width={16} height={16} color="#6941FF" />
              {isVerifyChatResponse ? "Verified" : t("systemChat.verifyButton")}
            </Button>
          </Flex>
        )}
    </Box>
  );
}

export default SystemChat;
